// Re-authentication modal for the Super Admin panel. Mounted once by
// SuperAdminLayout; it listens for the global `sa:reauth-required` event
// (fired from the axios 401 interceptor) and prompts for a fresh TOTP code
// without unmounting the current tab.
//
// If the admin cancels / escapes the modal, we treat it as "sign out" —
// clearing the SA token and bouncing back to the TOTP gate so the panel's
// content isn't left in a half-authenticated empty state.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck } from "lucide-react";

export default function TotpReauthModal() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => {
      // Debounce: multiple failed API calls fire the same event — only one
      // modal needed.
      setCode("");
      setOpen(true);
    };
    window.addEventListener("sa:reauth-required", handler);
    return () => window.removeEventListener("sa:reauth-required", handler);
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      // Call directly without the SA header so a stale token doesn't trigger
      // another 401 loop — just the base JWT is enough here.
      const { data } = await api.post("/super-admin/totp/login", { code: code.trim() });
      if (data?.token) {
        localStorage.setItem("bh_sa_token", data.token);
        toast.success("2FA sessie vernieuwd");
        setOpen(false);
        // Hard reload so every tab refetches its data cleanly with the new
        // token. Keeps us from manually plumbing retry logic through 5 tabs.
        window.location.reload();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code klopt niet");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    localStorage.removeItem("bh_sa_token");
    setOpen(false);
    toast.message("Je bent uitgelogd uit Super Admin");
    navigate("/brands");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-md px-4"
      data-testid="sa-reauth-modal"
      onKeyDown={(e) => { if (e.key === "Escape") cancel(); }}
    >
      <div className="w-full max-w-md bg-white rounded-[var(--global-radius-xl)] border border-gray-200 shadow-xl p-7">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 mb-3">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.75} />
          Sessie verlopen
        </div>
        <h2 className="font-display text-2xl font-medium text-[#1a1a1a] mb-2">
          Bevestig je 2FA-code
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Je Super Admin sessie is verlopen. Open je authenticator-app en voer de huidige 6-cijferige code in om verder te werken zonder je plek te verliezen.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            maxLength={6}
            inputMode="numeric"
            placeholder="123456"
            className="h-12 text-center font-mono text-xl tracking-[0.4em] bg-white"
            autoFocus
            data-testid="sa-reauth-code"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancel}
              data-testid="sa-reauth-cancel"
              className="h-11 flex-1 rounded-[var(--global-radius)]"
            >
              Uitloggen
            </Button>
            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="sa-reauth-submit"
              className="h-11 flex-1 rounded-[var(--global-radius)] bg-[var(--global-action)] text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Bezig…" : "Verifieer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
